import {
    DeleteOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    InboxOutlined,
    NumberOutlined,
    UpOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import InfoItemWithCopy from 'components/UI/InfoItemWithCopy';
import { OfferLabel } from 'components/UI/ProductCard/atoms/Typography/OfferLabel/OfferLabel';
import Quality from 'components/UI/Quality';
import { QualityColorEnum } from 'enums/qualityColor.enum';
import { isNil } from 'lodash';
import { useState } from 'react';
import { formatFullCurrency } from 'utils/number';
import * as S from './ProductCartCardMobile.styles';
import { IProductCartCardProps } from './types';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

const isValidQuality = (quality: string): quality is QualityColorEnum =>
  Object.values(QualityColorEnum).includes(quality as QualityColorEnum);

export default function ProductCartCardMobile({
  image,
  productName,
  producer,
  quality,
  packaging,
  deliveryDayLabel,
  packagesQuantity,
  totalUnits,
  packageValue,
  consolidatedUnitValue,
  unitValue,
  totalValue,
  shippingCost,
  onRemove,
  unitPerPacking = 0,
  observation = '',
  message = '',
  dimension = '',
  gfpLineId,
  colors = '',
  productCode = '',
  barCode = '',
  gfpDetails,
  offerType,
  conceptName,
}: IProductCartCardProps) {
  const [showMore, setShowMore] = useState(false);

  const isGfp = Boolean(gfpLineId);

  const gfpObs =
    gfpDetails?.qualityObservation1 || gfpDetails?.qualityObservation2 || '-';
  const gfpObsText =
    gfpDetails?.qualityObservation1 && gfpDetails?.qualityObservation2
      ? `${gfpDetails.qualityObservation1}; ${gfpDetails.qualityObservation2}`
      : gfpObs;

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <S.CardContainer>
      <S.Row className="product-title-row">
        <S.Col>
          <S.ProductImage src={image} alt={productName} />
        </S.Col>
        <S.Col>
          <S.ProductName>{productName}</S.ProductName>
          {conceptName && (
            <Tooltip title={conceptName}>
              <S.ConceptName>{conceptName}</S.ConceptName>
            </Tooltip>
          )}
          {offerType != null && <OfferLabel offerType={offerType} />}
          <S.InfoItem className="producer-info">
            <S.InfoLabel>Produtor:</S.InfoLabel>
            <S.Producer>{producer}</S.Producer>
          </S.InfoItem>
          {message && (
            <Tooltip title={message}>
              <S.NotConfirmedLabel>
                <ExclamationCircleOutlined /> Compra não confirmada
              </S.NotConfirmedLabel>
            </Tooltip>
          )}
        </S.Col>
      </S.Row>

      <S.Row>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Qualidade:</S.InfoLabel>
            <S.InfoValue>
              {isValidQuality(quality) ? (
                <Quality quality={quality} />
              ) : (
                quality
              )}
            </S.InfoValue>
          </S.InfoItem>
        </S.Col>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Embalagem:</S.InfoLabel>
            <S.InfoValue noWrap>{packaging}</S.InfoValue>
          </S.InfoItem>
        </S.Col>
        <S.Col flex="0 0 auto">
          <S.ShowMoreButton onClick={handleShowMore}>
            {showMore ? 'Exibir menos' : 'Exibir mais'}
            {showMore ? <UpOutlined /> : <DownOutlined />}
          </S.ShowMoreButton>
        </S.Col>
      </S.Row>

      {showMore && (
        <S.ExpandedSection>
          <S.Row>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Cores:</S.InfoLabel>
                <S.InfoValue>{colors || 'Não informado'}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Dimensão/Altura:</S.InfoLabel>
                <S.InfoValue>{dimension || 'Não informado'}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
          </S.Row>

          <S.Row>
            <S.Col>
              <InfoItemWithCopy
                label="Cód. do Produto"
                value={productCode}
                fallback="Não informado"
              />
            </S.Col>
            <S.Col>
              <InfoItemWithCopy
                label="Cód. de Barras"
                value={barCode}
                fallback="Não informado"
              />
            </S.Col>

            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Observação da Oferta:</S.InfoLabel>
                <S.InfoValue>{observation || '-'}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
          </S.Row>
        </S.ExpandedSection>
      )}

      {isGfp && !isNil(gfpDetails) && (
        <S.ExpandedSection>
          <S.Row>
            <S.ExpandedTitle>SOBRE A GFP</S.ExpandedTitle>
          </S.Row>

          <S.Row>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Entrega CVH:</S.InfoLabel>
                <S.InfoValue>{gfpDetails.deliveryDate}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Série:</S.InfoLabel>
                <S.InfoValue>{gfpDetails.series}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Número da GFP:</S.InfoLabel>
                <S.InfoValue>{gfpDetails.gfpNumber}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Lote:</S.InfoLabel>
                <S.InfoValue>{gfpDetails.lot}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Observações da GFP:</S.InfoLabel>
                <S.InfoValue>{gfpObsText ?? '-'}</S.InfoValue>
              </S.InfoItem>
            </S.Col>

            <S.Col>
              <S.InfoItem>
                <S.InfoLabel>Observações do Produtor:</S.InfoLabel>
                <S.InfoValue>{gfpDetails.observation ?? '-'}</S.InfoValue>
              </S.InfoItem>
            </S.Col>
          </S.Row>
        </S.ExpandedSection>
      )}

      <S.Row>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>
              <InboxOutlined /> Quant. Emb:
            </S.InfoLabel>
            <S.InfoValue>{packagesQuantity}</S.InfoValue>
          </S.InfoItem>
        </S.Col>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>
              <NumberOutlined /> Total Unid:
            </S.InfoLabel>
            <S.InfoValue>{totalUnits}</S.InfoValue>
          </S.InfoItem>
        </S.Col>
      </S.Row>

      <S.Row className="price-row">
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Valor da Embalagem:</S.InfoLabel>
            <S.PriceValue>{formatCurrency(packageValue)}</S.PriceValue>
          </S.InfoItem>
        </S.Col>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Valor Unit. Consolid:</S.InfoLabel>
            <S.PriceValue>{formatCurrency(consolidatedUnitValue)}</S.PriceValue>
          </S.InfoItem>
        </S.Col>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Valor Unitário:</S.InfoLabel>
            <S.PriceValue>{formatCurrency(unitValue)}</S.PriceValue>
          </S.InfoItem>
        </S.Col>
        <S.Col>
          <S.InfoItem>
            <S.InfoLabel>Valor Total:</S.InfoLabel>
            <S.TotalPrice>{formatCurrency(totalValue)}</S.TotalPrice>
          </S.InfoItem>
        </S.Col>
      </S.Row>

      <S.Row className="button-row">
        <S.Col>
          <S.FreightInfo>
            <span>Frete:</span>
            <span>{formatFullCurrency(shippingCost)}</span>
          </S.FreightInfo>
        </S.Col>
        <S.Col flex="0 0 auto">
          <S.RemoveButton icon={<DeleteOutlined />} onClick={onRemove}>
            Remover
          </S.RemoveButton>
        </S.Col>
      </S.Row>
    </S.CardContainer>
  );
}
